/**
 * Tutorial Store (AC-666)
 *
 * Zustand store for managing the Interactive Tutorial System state.
 * Handles tutorial progress, step navigation, and modal visibility.
 * Supports multiple tutorials via activeTutorialId.
 *
 * Technical Design Reference: docs/technical/TDD-AC-661-tutorial-system.md
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { TutorialStatus, TutorialState, TutorialActions, TutorialStore } from '../types/tutorial';
import apiService from '../services/api';
import { TOTAL_TUTORIAL_STEPS, TUTORIAL_ID, TUTORIAL_TOTAL_MAP } from '../config/tutorialConfig';

/**
 * Initial state for the tutorial store.
 */
const initialState: TutorialState = {
  status: 'not_started',
  currentStep: 0,
  totalSteps: TOTAL_TUTORIAL_STEPS,
  activeTutorialId: TUTORIAL_ID,
  isLoading: false,
  showWelcomeModal: false,
  showCompletionModal: false,
  isOverlayHidden: false,
  contextData: null,
};

/**
 * Tutorial store for managing interactive tutorial system.
 *
 * Uses optimistic updates for step advancement to provide
 * immediate feedback to users.
 */
export const useTutorialStore = create<TutorialStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      ...initialState,

      // =======================================================================
      // API ACTIONS
      // =======================================================================

      /**
       * Load tutorial state from the API for the default (core_workflow) tutorial.
       * Shows welcome modal if status is 'not_started'.
       */
      loadTutorialState: async () => {
        set({ isLoading: true });

        try {
          const response = await apiService.getTutorialProgress();

          set({
            status: response.status,
            currentStep: response.current_step ?? 0,
            // Always use frontend config for totalSteps - backend may be outdated
            totalSteps: TOTAL_TUTORIAL_STEPS,
            activeTutorialId: TUTORIAL_ID,
            isLoading: false,
            // Show welcome modal for new users who haven't started the tutorial
            showWelcomeModal: response.status === 'not_started',
            contextData: response.context_data ?? null,
          });
        } catch (error) {
          // On error, keep default state and don't show welcome modal
          console.error('Failed to load tutorial state:', error);
          set({ isLoading: false });
        }
      },

      /**
       * Start the default (core_workflow) tutorial at step 1.
       * Closes welcome modal and updates API.
       */
      startTutorial: async () => {
        set({ isLoading: true });

        try {
          const response = await apiService.updateTutorialProgress({
            status: 'in_progress',
            current_step: 1,
          });

          set({
            status: response.status,
            currentStep: response.current_step ?? 1,
            activeTutorialId: TUTORIAL_ID,
            totalSteps: TOTAL_TUTORIAL_STEPS,
            isLoading: false,
            showWelcomeModal: false,
            isOverlayHidden: false,
          });
        } catch (error) {
          console.error('Failed to start tutorial:', error);
          set({ isLoading: false });
        }
      },

      /**
       * Start a specific tutorial by ID.
       * Sets the active tutorial and begins at step 1.
       */
      startTutorialById: async (tutorialId: string) => {
        set({ isLoading: true });

        const totalSteps = TUTORIAL_TOTAL_MAP[tutorialId] || TOTAL_TUTORIAL_STEPS;

        try {
          const response = await apiService.updateTutorialProgress({
            status: 'in_progress',
            current_step: 1,
          }, tutorialId);

          set({
            status: response.status,
            currentStep: response.current_step ?? 1,
            activeTutorialId: tutorialId,
            totalSteps,
            isLoading: false,
            showWelcomeModal: false,
            showCompletionModal: false,
            isOverlayHidden: false,
            contextData: null,
          });
        } catch (error) {
          console.error(`Failed to start tutorial ${tutorialId}:`, error);
          set({ isLoading: false });
        }
      },

      /**
       * Advance to the next step.
       * Uses optimistic update for immediate feedback.
       * Shows completion modal when reaching last step.
       * Completes tutorial when advancing from last step.
       */
      advanceStep: async () => {
        const { currentStep, totalSteps, activeTutorialId } = get();

        // If at or past last step, complete the tutorial and show completion modal
        if (currentStep >= totalSteps) {
          try {
            const response = await apiService.updateTutorialProgress({
              status: 'completed',
            }, activeTutorialId);

            set({
              status: response.status,
              currentStep: response.current_step ?? totalSteps,
              showCompletionModal: true, // Show completion modal when tutorial finishes
            });
          } catch (error) {
            console.error('Failed to complete tutorial:', error);
            // Still show completion modal even on API error
            set({ showCompletionModal: true });
          }
          return;
        }

        const nextStep = currentStep + 1;

        // Optimistic update: immediately show next step
        set({ currentStep: nextStep });

        // Update API in background
        try {
          await apiService.updateTutorialProgress({
            current_step: nextStep,
          }, activeTutorialId);
        } catch (error) {
          // Revert on error
          console.error('Failed to advance step:', error);
          set({ currentStep: currentStep });
        }
      },

      /**
       * Skip the current step (same behavior as advanceStep).
       * Functionally equivalent but semantically different.
       */
      skipStep: async () => {
        const { currentStep, totalSteps, activeTutorialId } = get();

        // If at or past last step, complete the tutorial and show completion modal
        if (currentStep >= totalSteps) {
          try {
            const response = await apiService.updateTutorialProgress({
              status: 'completed',
            }, activeTutorialId);

            set({
              status: response.status,
              currentStep: response.current_step ?? totalSteps,
              showCompletionModal: true,
            });
          } catch (error) {
            console.error('Failed to complete tutorial:', error);
            set({ showCompletionModal: true });
          }
          return;
        }

        const nextStep = currentStep + 1;

        // Optimistic update
        set({ currentStep: nextStep });

        try {
          await apiService.updateTutorialProgress({
            current_step: nextStep,
          }, activeTutorialId);
        } catch (error) {
          // Revert on error
          console.error('Failed to skip step:', error);
          set({ currentStep: currentStep });
        }
      },

      /**
       * Dismiss the entire tutorial.
       * Hides the overlay but keeps tutorial 'in_progress' on backend so it can be resumed.
       */
      dismissTutorial: async () => {
        set({
          isOverlayHidden: true,
          showWelcomeModal: false,
          showCompletionModal: false,
        });
      },

      /**
       * Resume a specific tutorial by ID, preserving the stored step.
       * Fetches progress from API instead of resetting to step 1.
       */
      resumeTutorialById: async (tutorialId: string) => {
        set({ isLoading: true });
        const totalSteps = TUTORIAL_TOTAL_MAP[tutorialId] || TOTAL_TUTORIAL_STEPS;
        try {
          const response = await apiService.getTutorialProgress(tutorialId);
          set({
            status: 'in_progress',
            currentStep: response.current_step ?? 1,
            activeTutorialId: tutorialId,
            totalSteps,
            isLoading: false,
            showWelcomeModal: false,
            showCompletionModal: false,
            isOverlayHidden: false,
            contextData: response.context_data ?? null,
          });
          return response;
        } catch (error) {
          console.error(`Failed to resume tutorial ${tutorialId}:`, error);
          // Fallback: start at step 1
          await get().startTutorialById(tutorialId);
          return null;
        }
      },

      /**
       * Update tutorial context data (project/workflow IDs) for resume navigation.
       */
      updateTutorialContext: async (contextData: Record<string, unknown>) => {
        const { activeTutorialId, contextData: existing } = get();
        if (!activeTutorialId) return;
        // Merge with existing context and update store
        const merged = { ...(existing ?? {}), ...contextData };
        set({ contextData: merged });
        try {
          await apiService.updateTutorialProgress(
            { context_data: merged },
            activeTutorialId
          );
        } catch (error) {
          console.error('Failed to update tutorial context:', error);
        }
      },

      /**
       * Restart the currently active tutorial from step 1.
       * Resets progress and closes completion modal.
       */
      restartTutorial: async () => {
        const { activeTutorialId } = get();
        set({ isLoading: true });

        try {
          const response = await apiService.restartTutorial(activeTutorialId);

          set({
            status: response.status,
            currentStep: response.current_step ?? 1,
            isLoading: false,
            showWelcomeModal: false,
            showCompletionModal: false,
            isOverlayHidden: false,
          });
        } catch (error) {
          console.error('Failed to restart tutorial:', error);
          set({ isLoading: false });
        }
      },

      /**
       * Restart a specific tutorial by ID.
       */
      restartTutorialById: async (tutorialId: string) => {
        const totalSteps = TUTORIAL_TOTAL_MAP[tutorialId] || TOTAL_TUTORIAL_STEPS;
        set({ isLoading: true });

        try {
          const response = await apiService.restartTutorial(tutorialId);

          set({
            status: response.status,
            currentStep: response.current_step ?? 1,
            activeTutorialId: tutorialId,
            totalSteps,
            isLoading: false,
            showWelcomeModal: false,
            showCompletionModal: false,
            isOverlayHidden: false,
            contextData: null,
          });
        } catch (error) {
          console.error(`Failed to restart tutorial ${tutorialId}:`, error);
          set({ isLoading: false });
        }
      },

      // =======================================================================
      // UI STATE ACTIONS
      // =======================================================================

      /**
       * Set welcome modal visibility.
       */
      setShowWelcomeModal: (show: boolean) => {
        set({ showWelcomeModal: show });
      },

      /**
       * Set completion modal visibility.
       */
      setShowCompletionModal: (show: boolean) => {
        set({ showCompletionModal: show });
      },

      /**
       * Hide the overlay without dismissing the tutorial.
       * Tutorial stays 'in_progress' on backend so it can be resumed.
       */
      hideOverlay: () => {
        set({ isOverlayHidden: true });
      },
    }),
    { name: 'tutorial-store' }
  )
);

// ==========================================================================
// SELECTORS
// ==========================================================================

/**
 * Select whether the tutorial is currently active (in_progress).
 */
export const selectIsTutorialActive = (state: TutorialStore): boolean =>
  state.status === 'in_progress';

/**
 * Select the current step number.
 */
export const selectCurrentStep = (state: TutorialStore): number => state.currentStep;

/**
 * Select the total number of steps.
 */
export const selectTotalSteps = (state: TutorialStore): number => state.totalSteps;

/**
 * Select the tutorial status.
 */
export const selectTutorialStatus = (state: TutorialStore): TutorialStatus => state.status;

/**
 * Select the active tutorial ID.
 */
export const selectActiveTutorialId = (state: TutorialStore): string => state.activeTutorialId;

/**
 * Select whether the welcome modal should be shown.
 */
export const selectShowWelcomeModal = (state: TutorialStore): boolean => state.showWelcomeModal;

/**
 * Select whether the completion modal should be shown.
 */
export const selectShowCompletionModal = (state: TutorialStore): boolean =>
  state.showCompletionModal;

/**
 * Select loading state.
 */
export const selectIsLoading = (state: TutorialStore): boolean => state.isLoading;

/**
 * Select whether the overlay is hidden (paused by user).
 */
export const selectIsOverlayHidden = (state: TutorialStore): boolean => state.isOverlayHidden;

export default useTutorialStore;
